import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import s from './PositionedTooltip.module.scss'

/**
 * Hover/focus tooltip with optional tap toggle for touch devices.
 * Wrap interactive targets (do not rely on native disabled button events alone).
 */
const PositionedTooltip = ({
	content,
	children,
	enabled = true,
	className = '',
}) => {
	const anchorRef = useRef(null)
	const [visible, setVisible] = useState(false)
	const [coords, setCoords] = useState({ top: 0, left: 0, placement: 'bottom' })

	const updatePosition = useCallback(() => {
		const anchor = anchorRef.current
		if (!anchor) return
		const rect = anchor.getBoundingClientRect()
		const gap = 8
		const estimatedHeight = 56
		const openAbove =
			rect.bottom + estimatedHeight + gap > window.innerHeight &&
			rect.top > window.innerHeight - rect.bottom
		const placement = openAbove ? 'top' : 'bottom'
		const left = rect.left + rect.width / 2
		const top = openAbove ? rect.top - gap : rect.bottom + gap
		setCoords({ top, left, placement })
	}, [])

	const show = useCallback(() => {
		if (!enabled || !content) return
		updatePosition()
		setVisible(true)
	}, [content, enabled, updatePosition])

	const hide = useCallback(() => setVisible(false), [])

	const toggle = useCallback(
		event => {
			if (!enabled || !content) return
			event.preventDefault()
			event.stopPropagation()
			setVisible(prev => {
				const next = !prev
				if (next) updatePosition()
				return next
			})
		},
		[content, enabled, updatePosition],
	)

	useLayoutEffect(() => {
		if (!visible) return undefined
		updatePosition()
		const onScrollOrResize = () => updatePosition()
		const onDocumentPointer = event => {
			if (anchorRef.current && !anchorRef.current.contains(event.target)) {
				hide()
			}
		}
		window.addEventListener('scroll', onScrollOrResize, true)
		window.addEventListener('resize', onScrollOrResize)
		document.addEventListener('pointerdown', onDocumentPointer)
		return () => {
			window.removeEventListener('scroll', onScrollOrResize, true)
			window.removeEventListener('resize', onScrollOrResize)
			document.removeEventListener('pointerdown', onDocumentPointer)
		}
	}, [hide, updatePosition, visible])

	const tooltipNode =
		visible && content
			? createPortal(
					<div
						className={`${s.tooltip} ${
							coords.placement === 'top' ? s.tooltipTop : s.tooltipBottom
						}`}
						style={{ top: coords.top, left: coords.left }}
						role="tooltip"
					>
						{content}
					</div>,
					document.body,
				)
			: null

	return (
		<span
			className={`${s.anchor}${className ? ` ${className}` : ''}`}
			ref={anchorRef}
			onMouseEnter={show}
			onMouseLeave={hide}
			onFocus={show}
			onBlur={hide}
			onClick={enabled && content ? toggle : undefined}
			onKeyDown={
				enabled && content
					? event => {
							if (event.key === 'Enter' || event.key === ' ') {
								toggle(event)
							}
						}
					: undefined
			}
		>
			{children}
			{tooltipNode}
		</span>
	)
}

export default PositionedTooltip
